import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { AlertTriangle } from 'lucide-react';

export interface ExcludedMatchItem {
  id: string;
  name: string;
  matchedRef: string;
  matchType: 'sku' | 'gtin' | 'shopifyVariantId';
}

interface ExcludedItemsConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  excludedItems: ExcludedMatchItem[];
  onIncludeAndImport: () => void;
  onContinueWithout: () => void;
  isLoading: boolean;
}

export const ExcludedItemsConfirmationDialog: React.FC<
  ExcludedItemsConfirmationDialogProps
> = ({
  open,
  onOpenChange,
  excludedItems,
  onIncludeAndImport,
  onContinueWithout,
  isLoading,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={isLoading ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            {t('components.import_flow.shopify.excluded_items_dialog_title')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'components.import_flow.shopify.excluded_items_dialog_description',
              { count: excludedItems.length }
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[250px]">
          <div className="space-y-2 pr-4">
            {excludedItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2"
              >
                <span className="truncate text-sm font-medium">
                  {item.name || '—'}
                </span>
                <span
                  className={cn(
                    'ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
                    item.matchType === 'sku'
                      ? 'bg-blue-100 text-blue-700'
                      : item.matchType === 'gtin'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-orange-100 text-orange-700'
                  )}
                >
                  {item.matchType === 'shopifyVariantId'
                    ? 'Variant ID'
                    : item.matchType.toUpperCase()}
                  : {item.matchedRef}
                </span>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onContinueWithout}
            disabled={isLoading}
          >
            {t(
              'components.import_flow.shopify.excluded_items_dialog_continue_without'
            )}
          </Button>
          <Button onClick={onIncludeAndImport} disabled={isLoading}>
            {isLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : null}
            {t(
              'components.import_flow.shopify.excluded_items_dialog_include_and_import'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
